//  Copyright (c) 2026 Molly Xue
//  Licensed under the MIT License

import { loadVideo } from './loadVideo/webCam.js';
import { handleScrollByGesture } from './handleGestureInteraction/handleScrollByHandGesture.js';
import {
  mapToScreen,
  hideCursor,
  showCursor,
} from './handleGestureInteraction/cursorController.js';
import { detectFistToOpenPalmClickGesture } from './handleGestureInteraction/clickByHandGesture.js';
import { detectModalGestures } from './handleGestureInteraction/gestureForImageModal.js';

// ── Device-aware video config ─────────────────────────────
function getVideoConfig() {
  const isLowEnd = navigator.hardwareConcurrency <= 4;
  const isMobile = window.innerWidth < 768;

  return {
    width: isMobile ? Math.min(window.innerWidth, 640) : window.innerWidth,
    height: isMobile ? Math.min(window.innerHeight, 480) : window.innerHeight,
    fps: isLowEnd || isMobile ? 20 : 30,
  };
}

// ── Helpers ───────────────────────────────────────────────
/**
 * True when a finger is extended (tip is further from the wrist
 * than its MCP knuckle, scaled by EXTENSION_RATIO), because the thumb MCP sits close to the wrist and
 * gives a near-zero distance.
 */
function isFingerExtended(tip, mcp, wrist) {
  const distTip = Math.hypot(tip[0] - wrist[0], tip[1] - wrist[1]);
  const distMcp = Math.hypot(mcp[0] - wrist[0], mcp[1] - wrist[1]);
  return distTip > distMcp * 1.1;
}

/**
 * True when all five digits are extended (open palm).
 * Uses the wrist landmark (index 0) as the common origin.
 */
function fiveFingersExtended(ann) {
  const wrist = ann.palmBase[0]; // palmBase[0] === wrist in handpose

  const fingers = [
    { tip: ann.thumb[3], mcp: ann.indexFinger[0] }, // thumb: compare to index MCP
    { tip: ann.indexFinger[3], mcp: ann.indexFinger[0] },
    { tip: ann.middleFinger[3], mcp: ann.middleFinger[0] },
    { tip: ann.ringFinger[3], mcp: ann.ringFinger[0] },
    { tip: ann.pinky[3], mcp: ann.pinky[0] },
  ];

  const extendedCount = fingers.filter(({ tip, mcp }) => isFingerExtended(tip, mcp, wrist)).length;

  const siFiveFingersExtended = extendedCount >= 5;
  console.log(
    `Extended fingers: ${extendedCount} → ${siFiveFingersExtended ? 'click gesture possible' : 'not click gesture'}`,
  );
  return siFiveFingersExtended;
}

/** Index-to-thumb distance in raw camera space. */
function indexThumbDist(ann) {
  return Math.hypot(
    ann.indexFinger[3][0] - ann.thumb[3][0],
    ann.indexFinger[3][1] - ann.thumb[3][1],
  );
}

async function startDetectionLoop(model, video, videoWidth, videoHeight) {
  async function runDetection() {
    // Draw hand landmarks
    const predictions = await model.estimateHands(video);

    if (predictions !== null && predictions.length > 0) {
      const prediction = predictions[0];
      const ann = prediction.annotations;

      const indexFingerTipX = ann.indexFinger[3][0];
      const indexFingerTipY = ann.indexFinger[3][1];

      // ── Cursor ───────────────────────────────────────────
      // ──  always follows index fingertip ──────────

      const { screenX, screenY } = mapToScreen(
        indexFingerTipX,
        indexFingerTipY,
        videoWidth,
        videoHeight,
      );
      showCursor();

      const allFingersExtended = fiveFingersExtended(ann);
      const pinchDist = indexThumbDist(ann);

      const treshold = 20;
      let scrollPinchActive = pinchDist <= treshold;

      // ── Priority chain ─────────────────────────────────
      if (allFingersExtended) {
        console.log('all tips visible: click gesture branch');
        // Branch A – 5 tips → fist-to-click gesture
        // (scroll is mutually exclusive: a closed fist hides some tips)
        detectFistToOpenPalmClickGesture(prediction);
        handleScrollByGesture(false, predictions); // ensure scroll is off
      } else if (scrollPinchActive) {
        console.log('scroll pinch active: scroll gesture branch');
        // Branch B – index+thumb pinch → scroll
        handleScrollByGesture(true, predictions);
      } else {
        console.log('default branch: no pinch, maybe modal gesture + image hover');
        // Branch C – check pinky+thumb modal gesture + image hover
        handleScrollByGesture(false, predictions);
        if (!isNaN(screenX) && !isNaN(screenY)) {
          detectModalGestures(prediction, screenX, screenY);
        }
      }

      /*  // ── Scroll ───────────────────────────────────────────
      handleScrollByGesture(scrollPinchActive, predictions);

      // ── Click gesture (five-finger cluster → open palm) ──
      if (!pinch) {
        detectFistToOpenPalmClickGesture(prediction);
      }

      // ── Modal gesture (pinch-hold) + image hover overlay ─
      if (screenX && screenY && !isNaN(screenX) && !isNaN(screenY)) {
        detectModalGestures(prediction, screenX, screenY);
      } */
    } else {
      // No hand detected — hide cursor, reset scroll state.
      hideCursor();
      handleScrollByGesture(false, []);
    }

    requestAnimationFrame(runDetection);
  }

  //model = await handpose.load();
  runDetection();
}

// ── Bootstrap ─────────────────────────────────────────────
async function main() {
  const cfg = getVideoConfig();
  const video = await loadVideo(cfg.width, cfg.height, cfg.fps);

  // Dimensions are definitely available here
  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;

  // Load model AFTER video is ready, then kick off the loop
  const model = await handpose.load();

  startDetectionLoop(model, video, videoWidth, videoHeight);
}

main();
